import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  FormGroup,
  Label,
  Badge,
} from "reactstrap";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AdsService from "../../../services/OrderingServices/AdsService";
import DeviceAdsManagerService from "../../../services/OrderingServices/DeviceAdsManagerService";

const KioskAdManager = () => {
  const [allAds, setAllAds] = useState([]);
  const [selectedAds, setSelectedAds] = useState([]);
  const [draggedAd, setDraggedAd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [macId, setMacId] = useState("");

  useEffect(() => {
    fetchAllAds();
  }, []);

  async function fetchAllAds() {
    try {
      setLoading(true);
      const response = await AdsService.adsList({ pageSize: 100 });
      const adsList = Array.isArray(response) ? response : response?.data || [];
      setAllAds(adsList);
    } catch (error) {
      toast.error("Failed to fetch ads");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleDragStart = (ad) => {
    setDraggedAd(ad);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedAd && !selectedAds.find((item) => item.adId === draggedAd.id)) {
      setSelectedAds([
        ...selectedAds,
        {
          adId: draggedAd.id,
          macId: macId,
          orderIndex: selectedAds.length,
          ad: draggedAd,
        },
      ]);
      toast.success(`${draggedAd.title} added to queue`);
    } else if (
      draggedAd &&
      selectedAds.find((item) => item.adId === draggedAd.id)
    ) {
      toast.error("This ad is already in the queue");
    }
    setDraggedAd(null);
  };

  const removeAd = (adId) => {
    const newAds = selectedAds.filter((item) => item.adId !== adId);
    // Update orderIndex for remaining ads
    const updatedAds = newAds.map((ad, index) => ({
      ...ad,
      orderIndex: index,
    }));
    setSelectedAds(updatedAds);
    toast.success("Ad removed from queue");
  };

  const moveAdUp = (index) => {
    if (index > 0) {
      const newAds = [...selectedAds];
      [newAds[index], newAds[index - 1]] = [newAds[index - 1], newAds[index]];
      newAds.forEach((ad, idx) => {
        ad.orderIndex = idx;
      });
      setSelectedAds(newAds);
    }
  };

  const moveAdDown = (index) => {
    if (index < selectedAds.length - 1) {
      const newAds = [...selectedAds];
      [newAds[index], newAds[index + 1]] = [newAds[index + 1], newAds[index]];
      newAds.forEach((ad, idx) => {
        ad.orderIndex = idx;
      });
      setSelectedAds(newAds);
    }
  };

  const handleSubmit = async () => {
    if (!macId.trim()) {
      toast.error("Please enter a Device MAC ID");
      return;
    }
    if (selectedAds.length === 0) {
      toast.error("Please add at least one ad to the queue");
      return;
    }

    try {
      setSubmitting(true);
      const payload = selectedAds.map((item) => ({
        orderIndex: item.orderIndex,
        macId: macId,
        adId: item.adId,
      }));

      await DeviceAdsManagerService.bulkAssignAds(payload);
      toast.success(
        `Successfully assigned ${payload.length} ad(s) to device ${macId}`,
      );
      setSelectedAds([]);
      setMacId("");
    } catch (error) {
      toast.error("Failed to assign ads to device");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    const baseUrl =
      window.APP_CONFIG?.api3?.baseapi ||
      "https://api-databasewebservice.danfesolution.com";
    return `${baseUrl}${path}`;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Kiosk Ad Manager" pageTitle="Ads Management" />

          {/* Device Configuration */}
          <Row className="">
            <Col lg="12">
              <Card>
                <CardHeader
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <h5 className="mb-0">Device Configuration</h5>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col lg="6">
                      <FormGroup>
                        <Label for="macId" className="form-label">
                          Device MAC ID <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          id="macId"
                          placeholder="Enter Device MAC ID (e.g., 145D346BDE3F)"
                          value={macId}
                          onChange={(e) => setMacId(e.target.value)}
                          disabled={submitting}
                          style={{ borderColor: "#d0d0d0" }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Kiosk Ad Manager */}
          <Row>
            <Col lg="5">
              <Card className="border-0 shadow-sm">
                <CardHeader
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <h5 className="mb-0">Advertisement Library</h5>
       
                </CardHeader>
                <CardBody style={{ minHeight: "680px", maxHeight: "680px", overflowY: "auto" }}>
                  {loading ? (
                    <div className="text-center py-5">
                      <i
                        className="ri-loader-4-line ri-spin"
                        style={{ fontSize: "40px", color: "#556ee6" }}
                      ></i>
                    </div>
                  ) : allAds.length > 0 ? (
                    <div
                      className="ads-library"
                      style={{
                        // maxHeight: "600px",
                        overflowY: "auto",
                        padding: "8px",
                      }}
                    >
                      {allAds.length === 0 ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "48px 24px",
                            color: "#718096",
                          }}
                        >
                          <svg
                            width="64"
                            height="64"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{
                              margin: "0 auto 16px",
                              opacity: 0.3,
                            }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                            />
                          </svg>
                          <p style={{ fontSize: "16px", margin: 0 }}>
                            No ads available
                          </p>
                        </div>
                      ) : (
                        allAds.map((ad) => (
                          <div
                            key={ad.id}
                            draggable
                            onDragStart={() => handleDragStart(ad)}
                            className="ad-library-card"
                            style={{
                              display: "flex",
                              gap: "16px",
                              padding: "16px",
                              marginBottom: "12px",
                              backgroundColor: "#ffffff",
                              border: "2px solid #e7ecf1",
                              borderRadius: "12px",
                              cursor: "grab",
                              transition:
                                "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = "#556ee6";
                              e.currentTarget.style.boxShadow =
                                "0 8px 24px rgba(85, 110, 230, 0.15)";
                              e.currentTarget.style.transform =
                                "translateY(-4px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "#e7ecf1";
                              e.currentTarget.style.boxShadow =
                                "0 2px 8px rgba(0, 0, 0, 0.04)";
                              e.currentTarget.style.transform = "translateY(0)";
                            }}
                          >
                            {/* Thumbnail Section */}
                            <div style={{ flexShrink: 0 }}>
                              <div
                                style={{
                                  width: "96px",
                                  height: "96px",
                                  background: ad.mediaPath
                                    ? "transparent"
                                    : "linear-gradient(135deg, #f0f2f6 0%, #e7ecf1 100%)",
                                  borderRadius: "10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: ad.mediaPath
                                    ? "none"
                                    : "2px dashed #c5cfe7",
                                  overflow: "hidden",
                                }}
                              >
                                {ad.mediaPath ? (
                                  <img
                                    src={getImageUrl(ad.mediaPath)}
                                    alt={ad.title}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: "10px",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.parentElement.style.background =
                                        "linear-gradient(135deg, #f0f2f6 0%, #e7ecf1 100%)";
                                      e.target.parentElement.style.border =
                                        "2px dashed #c5cfe7";
                                    }}
                                  />
                                ) : (
                                  <svg
                                    width="40"
                                    height="40"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    style={{ color: "#556ee6", opacity: 0.6 }}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={1.5}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>

                            {/* Content Section */}
                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                minWidth: 0,
                              }}
                            >
                              {/* Title & Description */}
                              <div style={{ marginBottom: "12px" }}>
                                <h6
                                  style={{
                                    margin: "0 0 6px 0",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    color: "#1a202c",
                                    lineHeight: "1.4",
                                  }}
                                >
                                  {ad.title}
                                </h6>
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: "14px",
                                    color: "#718096",
                                    lineHeight: "1.5",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {ad.description || "No description available"}
                                </p>
                              </div>

                              {/* Metadata Tags */}
                              <div
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                }}
                              >
                                {/* Duration Badge */}
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    color: "#ffffff",
                                    fontWeight: "600",
                                    backgroundColor: "#556ee6",
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    letterSpacing: "0.3px",
                                  }}
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                                    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                                  </svg>
                                  {ad.durationSeconds}s
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <p style={{ color: "#adb5bd" }}>No ads available</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col lg="7">
              <Card className="border-0 shadow-sm h-100">
                <CardHeader
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #e0e0e0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h5 className="mb-0">Kiosk Display Queue</h5>
                  <Badge color={macId.trim() ? "primary" : "secondary"}>
                    {macId.trim() || "No Device"}
                  </Badge>
                </CardHeader>
                <CardBody
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "680px",
                    overflowY: "auto" 
                  }}
                >
                  {/* Drop Zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    style={{
                      flex: 1,
                      border: "3px dashed #c5cfe7",
                      borderRadius: "12px",
                      padding: "20px",
                      marginBottom: "20px",
                      background:
                        "linear-gradient(135deg, #f0f2f6 0%, #e7ecf1 100%)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent:
                        selectedAds.length === 0 ? "center" : "flex-start",
                      minHeight: "300px",
                    }}
                  >
                    {selectedAds.length === 0 ? (
                      <div style={{ textAlign: "center" }}>
                        <svg
                          width="72"
                          height="72"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          style={{
                            color: "#556ee6",
                            marginBottom: "20px",
                            filter:
                              "drop-shadow(0 4px 12px rgba(85, 110, 230, 0.2))",
                          }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <p
                          style={{
                            margin: "0 0 8px 0",
                            fontSize: "20px",
                            fontWeight: "700",
                            background:
                              "linear-gradient(135deg, #556ee6 0%, #4851df 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          Drop ads here
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#556ee6",
                            fontWeight: "500",
                          }}
                        >
                          Drag from the library on the left
                        </p>
                      </div>
                    ) : (
                      <div style={{ width: "100%" }}>
                        {selectedAds.map((item, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              gap: "16px",
                              padding: "16px",
                              marginBottom: "12px",
                              backgroundColor: "#ffffff",
                              border: "2px solid #556ee6",
                              borderRadius: "12px",
                              alignItems: "center",
                              boxShadow: "0 4px 12px rgba(85, 110, 230, 0.15)",
                              transition:
                                "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow =
                                "0 6px 16px rgba(85, 110, 230, 0.2)";
                              e.currentTarget.style.transform =
                                "translateY(-2px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow =
                                "0 4px 12px rgba(85, 110, 230, 0.15)";
                              e.currentTarget.style.transform = "translateY(0)";
                            }}
                          >
                            {/* Thumbnail */}
                            <div style={{ flexShrink: 0 }}>
                              <div
                                style={{
                                  width: "64px",
                                  height: "64px",
                                  background: item.ad.mediaPath
                                    ? "transparent"
                                    : "linear-gradient(135deg, #f0f2f6 0%, #e7ecf1 100%)",
                                  borderRadius: "8px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: item.ad.mediaPath
                                    ? "none"
                                    : "2px dashed #c5cfe7",
                                  overflow: "hidden",
                                }}
                              >
                                {item.ad.mediaPath ? (
                                  <img
                                    src={getImageUrl(item.ad.mediaPath)}
                                    alt={item.ad.title}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.parentElement.style.background =
                                        "linear-gradient(135deg, #f0f2f6 0%, #e7ecf1 100%)";
                                      e.target.parentElement.style.border =
                                        "2px dashed #c5cfe7";
                                    }}
                                  />
                                ) : (
                                  <svg
                                    width="28"
                                    height="28"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    style={{ color: "#556ee6", opacity: 0.5 }}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={1.5}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>

                            {/* Ad Info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <h6
                                style={{
                                  margin: "0 0 6px 0",
                                  fontSize: "15px",
                                  fontWeight: "600",
                                  color: "#1a202c",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  lineHeight: "1.4",
                                }}
                              >
                                {item.ad.title}
                              </h6>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                }}
                              >
                                {/* Duration Badge */}
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    color: "white",
                                    backgroundColor: "#556ee6",
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    letterSpacing: "0.3px",
                                  }}
                                >
                                  <svg
                                    width="12"
                                    height="12"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                                    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                                  </svg>
                                  {item.ad.durationSeconds}s
                                </span>
                              </div>
                            </div>

                            {/* Controls */}
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                flexShrink: 0,
                              }}
                            >
                              {/* Move Up Button */}
                              <button
                                onClick={() => moveAdUp(index)}
                                disabled={index === 0}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  border:
                                    index === 0
                                      ? "2px solid #e2e8f0"
                                      : "2px solid #556ee6",
                                  borderRadius: "8px",
                                  backgroundColor: "#ffffff",
                                  cursor:
                                    index === 0 ? "not-allowed" : "pointer",
                                  opacity: index === 0 ? 0.4 : 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  color: index === 0 ? "#a0aec0" : "#556ee6",
                                  fontSize: "16px",
                                }}
                                onMouseEnter={(e) => {
                                  if (index > 0) {
                                    e.currentTarget.style.backgroundColor =
                                      "#556ee6";
                                    e.currentTarget.style.color = "white";
                                    e.currentTarget.style.transform =
                                      "translateY(-2px)";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 8px rgba(85, 110, 230, 0.3)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#ffffff";
                                  e.currentTarget.style.color =
                                    index === 0 ? "#a0aec0" : "#556ee6";
                                  e.currentTarget.style.transform =
                                    "translateY(0)";
                                  e.currentTarget.style.boxShadow = "none";
                                }}
                              >
                                <i className="ri-arrow-up-s-line"></i>
                              </button>

                              {/* Move Down Button */}
                              <button
                                onClick={() => moveAdDown(index)}
                                disabled={index === selectedAds.length - 1}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  border:
                                    index === selectedAds.length - 1
                                      ? "2px solid #e2e8f0"
                                      : "2px solid #556ee6",
                                  borderRadius: "8px",
                                  backgroundColor: "#ffffff",
                                  cursor:
                                    index === selectedAds.length - 1
                                      ? "not-allowed"
                                      : "pointer",
                                  opacity:
                                    index === selectedAds.length - 1 ? 0.4 : 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  color:
                                    index === selectedAds.length - 1
                                      ? "#a0aec0"
                                      : "#556ee6",
                                  fontSize: "16px",
                                }}
                                onMouseEnter={(e) => {
                                  if (index < selectedAds.length - 1) {
                                    e.currentTarget.style.backgroundColor =
                                      "#556ee6";
                                    e.currentTarget.style.color = "white";
                                    e.currentTarget.style.transform =
                                      "translateY(-2px)";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 8px rgba(85, 110, 230, 0.3)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#ffffff";
                                  e.currentTarget.style.color =
                                    index === selectedAds.length - 1
                                      ? "#a0aec0"
                                      : "#556ee6";
                                  e.currentTarget.style.transform =
                                    "translateY(0)";
                                  e.currentTarget.style.boxShadow = "none";
                                }}
                              >
                                <i className="ri-arrow-down-s-line"></i>
                              </button>

                              {/* Remove Button */}
                              <button
                                onClick={() => removeAd(item.adId)}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  border: "2px solid #fc6180",
                                  borderRadius: "8px",
                                  backgroundColor: "#fff5f7",
                                  color: "#fc6180",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  fontSize: "16px",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#fc6180";
                                  e.currentTarget.style.color = "white";
                                  e.currentTarget.style.transform =
                                    "scale(1.05)";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 8px rgba(252, 97, 128, 0.3)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#fff5f7";
                                  e.currentTarget.style.color = "#fc6180";
                                  e.currentTarget.style.transform = "scale(1)";
                                  e.currentTarget.style.boxShadow = "none";
                                }}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Queue Summary */}
                  {selectedAds.length > 0 && (
                    <div
                      style={{
                        padding: "12px",
                        backgroundColor: "#f0f2f6",
                        border: "1px solid #dee2e6",
                        borderRadius: "8px",
                        marginBottom: "16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#212529",
                            fontWeight: "600",
                          }}
                        >
                          {selectedAds.length} ad
                          {selectedAds.length !== 1 ? "s" : ""} in queue
                        </p>
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontSize: "12px",
                            color: "#6c757d",
                          }}
                        >
                          Total duration:{" "}
                          {selectedAds.reduce(
                            (sum, item) => sum + (item.ad.durationSeconds || 0),
                            0,
                          )}{" "}
                          seconds
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={
                      !macId.trim() || selectedAds.length === 0 || submitting
                    }
                    className="w-100"
                    size="lg"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line ri-spin me-2"></i>
                        Assigning ads...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-cloud-2-line me-2"></i>
                        Assign Ads to Device
                      </>
                    )}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default KioskAdManager;
